import { useFieldArray, useFormContext } from 'react-hook-form';
import { Plus, Bus } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.tsx';
import NoItems from '@/components/ui/NoItems.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import ToggleInput from '@/components/form/ToggleInput.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import { territorySelectOptions } from '@/features/dashboard/tfg/types/Territory.utils.ts';
import {
  TransportInputForm,
  emptyTransportVariant,
} from '@/features/dashboard/tourTransport/schemas/transportSchema.ts';
import { TransportOffering } from '@/features/dashboard/tourTransport/types/TransportOffering.ts';
import { TransportSelectionMode } from '@/features/dashboard/tourTransport/types/TransportSelectionMode.ts';
import { transportOfferingToggleOptions } from '@/features/dashboard/tourTransport/types/TransportOffering.utils.ts';
import { transportSelectionModeToggleOptions } from '@/features/dashboard/tourTransport/types/TransportSelectionMode.utils.ts';
import TransportVariantForm from '@/features/dashboard/tourTransport/components/TransportVariantForm.tsx';

const TourTransportConfigCard = () => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<TransportInputForm>();

  const transportOffering = watch('transportOffering');

  const variantsFieldArray = useFieldArray({
    control,
    name: 'variants',
  });

  const offersTransport =
    transportOffering === TransportOffering.OFFERS_TRANSPORT;

  const handleOfferingChange = (value: string) => {
    if (value === TransportOffering.NO_TRANSPORT) {
      setValue('selectionMode', null);
      variantsFieldArray.replace([]);
    } else {
      if (!watch('selectionMode')) {
        setValue('selectionMode', TransportSelectionMode.MANDATORY);
      }
      if (variantsFieldArray.fields.length === 0) {
        variantsFieldArray.append(emptyTransportVariant());
      }
    }
  };

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle>Transport</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <SingleSelectInput
          fieldName="territory"
          label="Terytorium docelowe (UFG)"
          placeholder="Wybierz terytorium"
          required
          control={control}
          options={territorySelectOptions}
          error={errors.territory?.message}
        />

        <ToggleInput
          fieldPath="transportOffering"
          label="Czy oferujesz transport na ten wyjazd?"
          required
          options={transportOfferingToggleOptions}
          control={control}
          onChange={handleOfferingChange}
          error={errors.transportOffering?.message}
        />

        {offersTransport && (
          <>
            <ToggleInput
              fieldPath="selectionMode"
              label="Sposób wyboru transportu"
              required
              options={transportSelectionModeToggleOptions}
              control={control}
              error={errors.selectionMode?.message}
            />

            <div className="space-y-2">
              {variantsFieldArray.fields.length === 0 ? (
                <NoItems
                  icon={<Bus />}
                  title="Brak wariantów transportu"
                  description="Dodaj co najmniej jeden wariant transportu."
                />
              ) : (
                variantsFieldArray.fields.map((field, index) => (
                  <TransportVariantForm
                    key={field.id}
                    index={index}
                    remove={variantsFieldArray.remove}
                  />
                ))
              )}

              <InputError
                error={
                  errors.variants?.message ?? errors.variants?.root?.message
                }
              />

              <div className="pt-1">
                <ActionButton
                  tone="outline"
                  size="xs"
                  onClick={() =>
                    variantsFieldArray.append(emptyTransportVariant())
                  }
                >
                  <Plus />
                  Dodaj wariant transportu
                </ActionButton>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TourTransportConfigCard;
